/**
 * Campaign cover photo. Discovery tiles are 3:4 portrait.
 *
 * Any JPEG, PNG or WebP can be dropped. A crop window frames it to 3:4
 * before the file is sent, so a landscape screenshot is not a dead end.
 */

export const COVER_RATIO = 3 / 4;
export const COVER_RATIO_TOLERANCE = 0.03;
export const COVER_MAX_BYTES = 8 * 1024 * 1024;
export const COVER_MIMES = ["image/jpeg", "image/png", "image/webp"] as const;
export const COVER_ACCEPT = COVER_MIMES.join(",");
export const COVER_OUT_WIDTH = 1200;
export const COVER_OUT_HEIGHT = 1600;
export const COVER_FRAME_WIDTH = 240;
export const COVER_FRAME_HEIGHT = 320;

export function isCoverRatio(width: number, height: number): boolean {
  if (!(width > 0) || !(height > 0)) return false;
  return Math.abs(width / height - COVER_RATIO) <= COVER_RATIO_TOLERANCE;
}

/** Browsers (especially iOS) often hand over a photo with an empty type. */
export function coverMime(file: { type: string; name: string }): string | null {
  if (COVER_MIMES.includes(file.type as (typeof COVER_MIMES)[number])) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png") return "image/png";
  if (ext === "webp") return "image/webp";
  return null;
}

/** Type and size only. Ratio is fixed in the crop window. */
export function coverFileIssue(file: File): string | null {
  if (!coverMime(file)) return "Use a JPEG, PNG or WebP image.";
  if (file.size > COVER_MAX_BYTES) return "Cover photos must be 8 MB or smaller.";
  return null;
}

/** @deprecated Use coverFileIssue; the crop window handles ratio. */
export function coverRatioIssue(file: File, width: number, height: number): string | null {
  const fileIssue = coverFileIssue(file);
  if (fileIssue) return fileIssue;
  if (!isCoverRatio(width, height)) {
    return "Cover photos must be 3:4 portrait (for example 1200 × 1600).";
  }
  return null;
}

export function readImageSize(file: File): Promise<{ width: number; height: number; previewUrl: string }> {
  return new Promise((resolve, reject) => {
    const previewUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight, previewUrl });
    };
    img.onerror = () => {
      URL.revokeObjectURL(previewUrl);
      reject(new Error("That file could not be read as an image."));
    };
    img.src = previewUrl;
  });
}

export function minCoverScale(
  imgW: number, imgH: number, frameW = COVER_FRAME_WIDTH, frameH = COVER_FRAME_HEIGHT,
): number {
  if (!(imgW > 0) || !(imgH > 0)) return 1;
  return Math.max(frameW / imgW, frameH / imgH);
}

export function clampCoverOffset(
  imgW: number, imgH: number, scale: number, x: number, y: number,
  frameW = COVER_FRAME_WIDTH, frameH = COVER_FRAME_HEIGHT,
): { x: number; y: number } {
  const sw = imgW * scale;
  const sh = imgH * scale;
  return {
    x: Math.min(0, Math.max(frameW - sw, x)),
    y: Math.min(0, Math.max(frameH - sh, y)),
  };
}

export function centeredCoverOffset(
  imgW: number, imgH: number, scale: number,
  frameW = COVER_FRAME_WIDTH, frameH = COVER_FRAME_HEIGHT,
): { x: number; y: number } {
  return clampCoverOffset(
    imgW, imgH, scale, (frameW - imgW * scale) / 2, (frameH - imgH * scale) / 2,
    frameW, frameH,
  );
}

export function coverSourceRect(
  imgW: number, imgH: number, scale: number, x: number, y: number,
  frameW = COVER_FRAME_WIDTH, frameH = COVER_FRAME_HEIGHT,
): { sx: number; sy: number; sw: number; sh: number } {
  const sx = Math.max(0, Math.min(imgW, -x / scale));
  const sy = Math.max(0, Math.min(imgH, -y / scale));
  const sw = Math.max(1, Math.min(imgW - sx, frameW / scale));
  const sh = Math.max(1, Math.min(imgH - sy, frameH / scale));
  return { sx, sy, sw, sh };
}
